import { computed } from 'vue'
import type { UserRead } from '@gridcon-energy/schaltungstool-backend'
import type { JwtPayload } from 'jwt-decode'
import { jwtDecode } from 'jwt-decode'
import dayjs from 'dayjs/esm'
import { useAuth, useAuthState } from '#imports'
import { Notify } from '~/lib/notify'
import { useI18n } from 'vue-i18n'
import { navigateTo } from '#app'
import { Routes } from '~/_routes'
import { injectBackendClient } from '~/api/backendClients'
import { injectHandleFetchError } from '~/plugins/vue-plugins/fetch-error-handler'
import { DateFormatsEnum, formatDate } from '~/helper/date'
import { AuthRedirects } from '~/lib/auth'
import type { UserUpdate } from '@gridcon-energy/schaltungstool-backend/api'
import { defu } from 'defu'

export type UpdateUserOptions = {
  mergeSettings: boolean
  notify: boolean
}

export default function useUser() {
  const backend = injectBackendClient()
  const handleFetchError = injectHandleFetchError()
  const auth = useAuth()
  const authState = useAuthState()
  const { t } = useI18n()

  async function logout() {
    try {
      await auth.signOut({ callbackUrl: AuthRedirects.logout })
      Notify.success(t('AppActionLogoutSuccess'))
    } catch (e) {
      handleFetchError(e)
    }
  }

  function notifyTokenExpiration() {
    try {
      const decoded: JwtPayload = jwtDecode(auth.token.value || '')
      const expiresOn = new Date((decoded.exp as number) * 1000)
      const expiresIn = dayjs(expiresOn).diff(Date.now(), 'minutes')
      const expiresOnHuman = formatDate(expiresOn, DateFormatsEnum.DATE_TIME_SHORT)
      Notify.info(t('LoginSessionExpires', { minutes: expiresIn, date: expiresOnHuman }))
    } catch (e) {
      console.error(e)
    }
  }

  async function checkUsersTimeout() {
    if (!user.value?.admin) {
      return
    }
    try {
      const users = (await backend.users.getUsersAll()).data
      const usersTimedOut = users
        .filter((user) => dayjs(user.last_login).isBefore(dayjs().subtract(1, 'y')))
        .map((u) => u.id)
      if (!usersTimedOut.length) {
        return
      }
      Notify.info(t('UserManagementTimeOut', usersTimedOut.length), {
        action: {
          label: t('UserManagementTimeOutActionGoto'),
          onClick: () => {
            navigateTo({
              name: Routes.admin_user_management,
              query: { userIds: usersTimedOut.join(',') },
            })
          },
        },
      })
    } catch (e) {
      handleFetchError(e)
    }
  }

  /**
   * Unified endpoint to set current session/user
   */
  const user = computed<UserRead | null>({
    get: () => authState.data.value ?? null,
    set: (newValue) => (authState.data.value = newValue),
  })

  async function updateUser(update: UserUpdate, options?: Partial<UpdateUserOptions>): Promise<UserRead | null> {
    if (!user.value) {
      return null
    }
    const _options: UpdateUserOptions = defu(options, {
      mergeSettings: true,
      notify: false,
    })
    const _update: UserUpdate = update
    if (_options.mergeSettings) {
      _update.settings = defu(update.settings, user.value.settings)
      _update.design = defu(update.design, user.value.design)
    }
    try {
      const response = await backend.users.updateUserUsersUserIdPut(user.value.id, _update)
      user.value = response.data
      if (_options.notify) {
        Notify.success(t('SaveSuccessful'))
      }
      return response.data
    } catch (e) {
      handleFetchError(e)
      return null
    }
  }

  return {
    user: user,
    auth: auth,
    authState: authState,
    updateUser: updateUser,
    logout: logout,
    notifyTokenExpiration: notifyTokenExpiration,
    checkUsersTimeout: checkUsersTimeout,
  }
}
